<script setup lang="ts">
import { z, ZodIssueCode } from 'zod'
import { Form } from '@primevue/forms'
import { useToast } from '@/shared/composables/UseToast'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import { AdminPermissionResource, PermissionLevel, type UpdateCompanyFromAdminRequestDto } from '@swipe4work/models-orval'
import { scheduleAutoMatchForCompany, updateAdminCompany } from '@swipe4work/api-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useCompanyStore, ViewCompanyType } from '@/shared/stores/CompanyStore'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'
import { useConfirm } from 'primevue/useconfirm'
import InternalEmployerSelect from '@/shared/components/form/InternalEmployerSelect.vue'
import LinkableSalesDemoSelect from '@/shared/components/form/LinkableSalesDemoSelect.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'

const emits = defineEmits([ 'updateCompany' ])

const toast = useToast()
const queryClient = useQueryClient()

const { closeDialog, dialogData } = useDynamicDialogLifecycle<{
  companyId: number
  redirectBack: boolean
}>()

const companyStore = useCompanyStore()

const company = ref<ViewCompanyType>()
const initialAutoMatchEnabled = ref(false)

onMounted(async () => {
  company.value = await companyStore.getCompany(dialogData.value.companyId!)
  initialAutoMatchEnabled.value = company.value?.autoMatchEnabled ?? false
})

async function save(req: UpdateCompanyFromAdminRequestDto, scheduleExistingCandidates: boolean) {
  try {
    await updateAdminCompany(req)
    if (scheduleExistingCandidates) {
      await scheduleAutoMatchForCompany(dialogData.value.companyId!)
    }
    await queryClient.invalidateQueries({
      queryKey: [
        'admin',
        'admin-companies',
        'search',
      ],
    })
    toast.success('Bedrijf gewijzigd', 'Het bedrijf is succesvol gewijzigd')
    closeDialog()
    emits('updateCompany')
  }
  catch {
    toast.error('Error', 'Er is iets misgegaan bij het wijzigen van het bedrijf')
  }
}

async function onFormSubmit({ values, valid }: {
  values: any | undefined,
  valid: boolean
}) {
  if (!valid || !values) {
    toast.error('Validatiefout', 'Er zijn nog validatiefouten in het formulier')

    return
  }

  const req: UpdateCompanyFromAdminRequestDto = {
    companyId: dialogData.value.companyId!,
    dto: {
      name: values.name,
      assignedJobSlots: values.assignedJobSlots,
      accountManagerUuid: values.accountManagerUuid,
      salesPersonUuid: values.salesPersonUuid || null,
      mainEmployerId: values.mainEmployer || null,
      credits: values.credits,
      isHighlighted: values.isHighlighted || false,
      excludeFromStatistics: values.excludeFromStatistics || false,
      autoMatchEnabled: values.autoMatchEnabled || false,
      autoMatchMessage: values.autoMatchMessage || null,
      salesDemoId: values.salesDemoId || null,
      arrOverrideCents: values.arrOverrideEnabled && values.arrOverride != null
        ? Math.round(values.arrOverride * 100)
        : null,
    },
  }

  const wasAutoMatchDisabled = !initialAutoMatchEnabled.value
  const isAutoMatchNowEnabled = values.autoMatchEnabled === true

  if (wasAutoMatchDisabled && isAutoMatchNowEnabled) {
    confirm.require({
      message: 'Wil je ook alle huidige openstaande kandidaten automatisch matchen?',
      header: 'Auto-match activeren',
      acceptLabel: 'Ja, match openstaande kandidaten',
      rejectLabel: 'Nee, alleen auto-match voor nieuwe kandidaten',
      accept: () => save(req, true),
      reject: () => save(req, false),
    })
  }
  else {
    await save(req, false)
  }
}

const resolver = zodResolver(
  z.object({
    name: z.string({
      required_error: 'Bedrijfsnaam is verplicht',
    }).min(1, 'Bedrijfsnaam is verplicht'),
    assignedJobSlots: z.number().nullable(),
    credits: z.number({
    }).optional().nullable(),
    accountManagerUuid: z.string({
      message: 'Customer success is verplicht',
    }),
    salesPersonUuid: z.string().optional().nullable(),
    mainEmployer: z.number().optional(),
    isHighlighted: z.boolean().optional(),
    excludeFromStatistics: z.boolean().optional(),
    autoMatchEnabled: z.boolean().optional(),
    autoMatchMessage: z.string().optional().nullable(),
    salesDemoId: z.number().optional().nullable(),
    arrOverrideEnabled: z.boolean().optional(),
    arrOverride: z.number().min(0, 'De ARR override mag niet negatief zijn').optional().nullable(),
  }).required({
    name: true,
    accountManagerUuid: true,
  }).partial({
    assignedJobSlots: true,
    credits: true,
    salesPersonUuid: true,
    mainEmployer: true,
    isHighlighted: true,
    excludeFromStatistics: true,
    autoMatchEnabled: true,
    autoMatchMessage: true,
    salesDemoId: true,
    arrOverrideEnabled: true,
    arrOverride: true,
  }).superRefine((data, ctx) => {
    if (data.autoMatchEnabled && !data.autoMatchMessage?.trim()) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: [ 'autoMatchMessage' ],
        message: 'Auto-match bericht is verplicht als auto-match is ingeschakeld',
      })
    }
    if (data.arrOverrideEnabled && data.arrOverride == null) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: [ 'arrOverride' ],
        message: 'Vul een bedrag in, of zet de ARR override uit',
      })
    }
  }),
)
const confirm = useConfirm()

const deleteCompany = () => {
  closeDialog()
  companyStore.deleteCompany(company.value!.id, confirm, dialogData.value.redirectBack ?? false)
}

const hasSubscription = computed(() => {
  if (!company.value) {
    return false
  }

  if (company.value.type === 'Lead') {
    return false
  }

  return company.value.hasSubscription
})

const { can } = usePermissions()

// A company keeps the demo it came out of: linking is only offered while there is none.
const linkedSalesDemo = computed(() => company.value?.salesDemo ?? null)

// The linkable-demos endpoint sits behind SalesMetrics/Read, so hide the field without it.
const canLinkSalesDemo = computed(() => can(AdminPermissionResource.SalesMetrics, PermissionLevel.Read))
</script>

<template>
  <section class="min-w-90vw flex flex-col gap-y-7.5 lg:min-w-3xl md:min-w-xl">
    <h2 class="mt-5">
      Bedrijf wijzigen
    </h2>

    <Form
      v-if="company"
      :resolver
      v-slot="$form"
      @submit="onFormSubmit"
      class="w-full flex flex-col gap-4"
      :initial-values="{
        name: company.name,
        assignedJobSlots: company.companyPlan.assignedJobSlots,
        credits: company.companyPlan.matchCredits,
        accountManagerUuid: company.accountManager?.externalId,
        salesPersonUuid: company.salesPerson?.externalId ?? null,
        mainEmployer: company.mainEmployer?.id,
        isHighlighted: company.isHighlighted ?? false,
        excludeFromStatistics: company.statistics.excludeFromStatistics,
        autoMatchEnabled: company.autoMatchEnabled ?? false,
        autoMatchMessage: company.autoMatchMessage ?? null,
        salesDemoId: null,
        arrOverrideEnabled: company.arrOverrideCents != null,
        arrOverride: company.arrOverrideCents != null ? company.arrOverrideCents / 100 : null,
      }"
    >
      <FormField
        v-slot="$field"
        name="name"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InputText class="w-full" />
          <label for="name">Bedrijfsnaam</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="accountManagerUuid"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InternalEmployerSelect
            value-field="externalId"
            :department="['CustomerSuccess', 'Sales']"
            placeholder=""
          />
          <label for="accountManagerUuid">Customer Success</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="salesPersonUuid"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InternalEmployerSelect
            value-field="externalId"
            placeholder=""
          />
          <label for="salesPersonUuid">Sales persoon</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="mainEmployer"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <Select
            class="w-full"
            :options="company.users"
            option-label="fullName"
            option-value="id"
            :nullable="true"
          />
          <label for="mainEmployer">Contactpersoon</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <div v-if="linkedSalesDemo" class="flex flex-col gap-1">
        <label>Sales demo</label>
        <div class="flex items-center gap-2 rounded-md bg-surface-50 px-3 py-2">
          <i class="fa-regular fa-link text-surface-400" />
          <span class="text-sm">{{ linkedSalesDemo.name }}</span>
        </div>
        <small class="text-gray-500">
          Dit bedrijf is al aan een demo gekoppeld.
        </small>
      </div>

      <FormField
        v-else-if="canLinkSalesDemo"
        v-slot="$field"
        name="salesDemoId"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <LinkableSalesDemoSelect />
          <label for="salesDemoId">Sales demo koppelen</label>
        </IftaLabel>
        <small class="text-gray-500">
          Koppel een demo die nog geen bedrijf heeft. De demo wordt op deal won gezet en neemt het
          huidige abonnement van dit bedrijf over als definitief abonnement.
        </small>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <template v-if="!company.parentCompany">
        <h3>
          Quota
        </h3>

        <div class="grid grid-cols-2 gap-x-2">
          <FormField
            v-slot="$field"
            name="assignedJobSlots"
            as-child
            class="flex flex-col gap-1"
          >
            <div>
              <IftaLabel>
                <InputNumber class="w-full" :disabled="!hasSubscription" />
                <label for="assignedJobSlots">Aantal overschreven vacatures slots</label>
              </IftaLabel>

              <Message
                v-if="$field?.invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $field.error?.message }}
              </Message>
            </div>
          </FormField>

          <FormField
            v-slot="$field"
            name="credits"
            class="flex flex-col gap-1"
          >
            <div>
              <IftaLabel>
                <InputNumber
                  class="w-full"
                  :use-grouping="false"
                  fluid
                  :disabled="hasSubscription"
                />
                <label for="credits">Match-Credits</label>
              </IftaLabel>

              <Message
                v-if="$field?.invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $field.error?.message }}
              </Message>
            </div>
          </FormField>
        </div>

        <h3>
          Omzet
        </h3>

        <FormField
          v-slot="$field"
          name="arrOverrideEnabled"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <Checkbox binary input-id="arrOverrideEnabled" />
            <label for="arrOverrideEnabled">ARR overschrijven</label>
          </div>
          <small class="text-gray-500">
            Uit = de ARR volgt het lopende abonnement. Aan = het ingevulde bedrag telt als jaarwaarde
            van dit bedrijf.
          </small>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField
          v-if="$form.arrOverrideEnabled?.value"
          v-slot="$field"
          name="arrOverride"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <InputNumber
              class="w-full"
              mode="currency"
              currency="EUR"
              locale="nl-NL"
              :min="0"
              fluid
            />
            <label for="arrOverride">ARR override (per jaar)</label>
          </IftaLabel>

          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </template>

      <h3>
        Instellingen
      </h3>

      <FormField
        v-slot="$field"
        name="isHighlighted"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center gap-2">
          <Checkbox binary input-id="isHighlighted" />
          <label for="isHighlighted">Uitgelicht bedrijf</label>
        </div>
        <small class="text-gray-500">
          Uitgelichte bedrijven worden prominenter weergegeven op de branding pagina.
        </small>
        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="excludeFromStatistics"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center gap-2">
          <Checkbox binary input-id="excludeFromStatistics" />
          <label for="excludeFromStatistics">Uitsluiten van statistieken</label>
        </div>
        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="autoMatchEnabled"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center gap-2">
          <Checkbox
            binary
            input-id="autoMatchEnabled"
            :disabled="!hasSubscription"
          />
          <label for="autoMatchEnabled">Auto-match inschakelen</label>
          <small v-if="!hasSubscription" class="text-gray-500">
            (Alleen beschikbaar bij een actief abonnement)
          </small>
        </div>
        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="autoMatchMessage"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <Textarea
            class="w-full" rows="4"
            :disabled="!hasSubscription"
          />
          <label for="autoMatchMessage">Auto-match bericht</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <div class="flex justify-end gap-x-3">
        <Button
          v-permission="{ resource: AdminPermissionResource.Company, level: PermissionLevel.Delete }"
          label="Verwijder bedrijf"
          icon="fa-regular fa-trash"
          outlined
          severity="danger"
          @click="deleteCompany"
        />
        <Button
          label="Sluiten"
          severity="secondary"
          @click="closeDialog"
        />
        <Button
          v-permission="{ resource: AdminPermissionResource.Company, level: PermissionLevel.Write }"
          type="submit"
          label="Opslaan"
        />
      </div>
    </Form>
  </section>
</template>
